import {
  Ad,
  AdTypeEnum,
  PaidService,
  PaidServiceTargetTypeEnum,
  PaidTypeEnum,
  PaymentCreateStatusEnum,
  PaymentMethodEnum,
  PaymentResult
} from '@/__generated_graphql__/types';
import {
  MAPPED_TYPE_TO_SERVICE_KEY,
  WALLET_METHOD_ID
} from '@/components/AdvertisePayments/AdvertisePayments.constants';
import { MappedServiceType } from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.types';
import { AD_TYPE, ServiceKey } from '@/constants/common';
import { CardAd } from '@/types';

export const createFailedResult = (
  code: string,
  message: string
): PaymentResult => ({
  status: PaymentCreateStatusEnum.Failure,
  paymentId: null,
  redirectUrl: null,
  errors: [
    {
      code,
      message,
      path: null
    }
  ]
});

// Postpaid credit belongs to the company, so it may only back company
// (vacancy) payments — never a candidate's resume publication/promotion.
export const isCompanyModeAd = (ad?: Pick<CardAd, '__typename'>): boolean =>
  // eslint-disable-next-line no-underscore-dangle
  ad?.__typename === AD_TYPE.VACANCY;

export const mapTypenameToAdType = (typename: Ad['__typename']): AdTypeEnum => {
  switch (typename) {
    case 'Vacancy':
      return AdTypeEnum.Vacancy;
    case 'Resume':
      return AdTypeEnum.Resume;
    default:
      throw new Error(`[Advertisement] Unsupported typename: ${typename}`);
  }
};

export const mapTypenameToPaidServiceTargetType = (
  typename: Ad['__typename']
): PaidServiceTargetTypeEnum => {
  switch (typename) {
    case 'Vacancy':
      return PaidServiceTargetTypeEnum.Vacancy;
    case 'Resume':
      return PaidServiceTargetTypeEnum.Resume;
    default:
      return PaidServiceTargetTypeEnum.User;
  }
};

export const selectWalletBalance = (services: PaidService[]): number => {
  const TARGET_ID = WALLET_METHOD_ID.toLowerCase();

  for (const { paymentMethods = [] } of services) {
    const wallet = paymentMethods.find(
      method =>
        String(method?.id ?? '')
          .trim()
          .toLowerCase() === TARGET_ID
    );

    if (wallet && typeof wallet.balance === 'number') {
      return wallet.balance;
    }
  }

  return 0;
};

export const selectOptionPrice = (
  services: PaidService[],
  optionId: string
): number | null => {
  for (const service of services) {
    const options = service.options ?? [];
    const match = options.find(option => option?.id === optionId);

    if (match && match.price) {
      return match.price;
    }
  }
  return null;
};

// Prepaid must cover the price; postpaid may go negative down to -postpaidLimit.
export const canPayWithWallet = (
  walletBalance: number,
  optionPrice: number,
  paidType?: PaidTypeEnum | null,
  postpaidLimit?: number | null
): boolean => {
  const allowedNegativeBalance =
    paidType === PaidTypeEnum.Postpaid ? (postpaidLimit ?? 0) : 0;

  return walletBalance - optionPrice >= -allowedNegativeBalance;
};

export const isWalletMethod = (method: PaymentMethodEnum): boolean => {
  return (
    method === PaymentMethodEnum.Wallet ||
    String(method).toLowerCase() === WALLET_METHOD_ID
  );
};

export const extractFirstNumber = (inputString: string) => {
  const matchedNumbers = String(inputString).match(/\d+/);
  return matchedNumbers ? parseInt(matchedNumbers[0], 10) : 0;
};

export const getServiceKeyByType = (
  type?: MappedServiceType | null
): ServiceKey | null => (type ? MAPPED_TYPE_TO_SERVICE_KEY[type] : null);
